import { deepBlueSky } from "@/styles/theme";
import {
  Avatar,
  Box,
  Button,
  Center,
  Container,
  Divider,
  Grid,
  Heading,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import videoThumbEn from "@root/public/images/testimonial-thumb-en.png";
import videoThumbPt from "@root/public/images/testimonial-thumb-pt.jpeg";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { useEffect, useState } from "react";
import { AbsoluteOverlay } from "../LandingPage/AbsoluteOverlay";
import { PlayButton } from "../LandingPage/PlayButton";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarIcon } from "lucide-react";
import { DashDivider } from "../LandingPage/DashDivider";
import Link from "next/link";

const TestimonialCard = ({
  avatar,
  testimonial,
  name,
  jobTitle,
}: {
  avatar: string;
  testimonial: string;
  name: string;
  jobTitle: string;
}) => {
  return (
    <Box p={6} w="full" fontFamily={"Gotham"}>
      <HStack gap={4} mb={8}>
        <Avatar
          size={{ base: "lg", md: "lg" }}
          src={avatar}
          name={name}
          boxShadow={"sm"}
        />
        <Box>
          <Text fontSize="1.4rem" color="white" fontWeight={600}>
            {name}
          </Text>
          <Text fontSize="1.1rem" color="white" fontWeight={400} noOfLines={2}>
            {jobTitle}
          </Text>
        </Box>
      </HStack>
      <Text
        mb={4}
        fontSize={{
          base: "lg",
          md: "xl",
        }}
        maxW="full"
        color="white"
        noOfLines={11}
      >
        <Text as="span" color="brand.900">
          &quot;
        </Text>
        <Text as="span" dangerouslySetInnerHTML={{ __html: testimonial }} />
        <Text as="span" color="brand.900">
          &quot;
        </Text>
      </Text>
    </Box>
  );
};

export const CoacheeTestimonials = () => {
  const t = useTranslations("CoacheePage.testimonials");
  const [isOpen, setIsOpen] = useState(false);
  const locale = useLocale();

  const [videoUrl, setVideoUrl] = useState("");

  useEffect(() => {
    setVideoUrl(
      window.location.origin +
        (locale === "pt"
          ? "/videos/video-testimonial-pt.mp4"
          : "/videos/video-testimonial-en.mp4")
    );
  }, [locale]);

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  };

  return (
    <Box
      bgColor={deepBlueSky}
      py={{ base: 8, md: 16 }}
      px={{ base: 8, md: 24 }}
      color="white"
    >
      <Container maxW="container.xl">
        <Heading
          fontSize={"3xl"}
          fontWeight={600}
          textAlign={"center"}
          mb={8}
          dangerouslySetInnerHTML={{ __html: t.raw("title") }}
        />

        <Grid
          w="full"
          id="testimonials"
          maxW="full"
          overflow="hidden"
          gap={4}
          gridTemplateColumns={{
            base: "1fr",
            md: "1fr 1fr",
          }}
        >
          {/* Video Section */}
          <Box
            w="full"
            h="full"
            position="relative"
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={{ base: 4, md: 8 }}
          >
            <Center h="full" position="relative" w="full" flexDir={"column"}>
              <AnimatePresence mode="wait">
                {isOpen ? (
                  <motion.div
                    key="video"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    transition={{ duration: 0.3 }}
                    style={{ width: "100%", height: "100%" }}
                  >
                    <Box w="470px" h="full" minH="300px">
                      <iframe
                        src={`${videoUrl}?autoplay=1`}
                        title="Video Testimonial"
                        width="470px"
                        height="100%"
                        frameBorder="0"
                        allowFullScreen
                        allow="autoplay"
                      />
                    </Box>
                  </motion.div>
                ) : (
                  <motion.div
                    key="thumbnail"
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    transition={{ duration: 0.3 }}
                    style={{
                      width: "auto",
                      height: "100%",
                      position: "relative",
                      borderRadius: "1rem",
                    }}
                  >
                    <Image
                      src={locale === "pt" ? videoThumbPt : videoThumbEn}
                      alt="Video Thumb"
                      quality={80}
                      style={{
                        width: "470px",
                        height: "100%",
                        borderRadius: "1rem",
                      }}
                    />
                    <AbsoluteOverlay borderRadius={"1rem"}>
                      <PlayButton onClick={() => setIsOpen(true)} />
                    </AbsoluteOverlay>
                  </motion.div>
                )}
              </AnimatePresence>
              <Text fontSize={"1.4rem"} fontWeight={600} color="white" mt={4}>
                {locale === "pt" ? "Maria Seruya" : "Patrick Devosse"}
              </Text>
              <Text fontSize={"1.1rem"} fontWeight={400} color="white">
                {locale === "pt" ? "Artista Motivacional" : "Entrepreneur"}
              </Text>
            </Center>
          </Box>

          {/* Testimonials Section */}
          <VStack
            w="full"
            h="full"
            position="relative"
            p={{ base: 4, md: 8 }}
            divider={<Divider />}
          >
            <TestimonialCard
              avatar="/images/avatars/smaranda.png"
              testimonial={t("testimonial1")}
              name="Smaranda Doucet"
              jobTitle={"CEO at Flowtrial"}
            />
            <TestimonialCard
              avatar=""
              testimonial={t("testimonial2")}
              name="Ana Henriques"
              jobTitle={t("testimonial2Title")}
            />
            <TestimonialCard
              avatar=""
              testimonial={t("testimonial3")}
              name="Nuno C."
              jobTitle={t("testimonial3Title")}
            />
          </VStack>
        </Grid>
        <Text
          fontSize={"1.4rem"}
          textAlign={"center"}
          fontWeight={600}
          mt={8}
          dangerouslySetInnerHTML={{ __html: t.raw("callToAction") }}
        />
        <VStack mt={8} gap={8}>
          <DashDivider color="white" />
          <Link href="#schedule">
            <Button
              size="lg"
              fontSize="2xl"
              w={{
                base: "full",
                md: "auto",
              }}
              h="auto"
              py={4}
              variant={"primary"}
              leftIcon={<CalendarIcon />}
              whiteSpace="normal"
              wordBreak="break-word"
            >
              {t("scheduleButton")}
            </Button>
          </Link>
        </VStack>
      </Container>
    </Box>
  );
};
